import {
    Component,
    Element,
    h,
    Host,
    Prop,
    Listen,
    Watch,
} from "@stencil/core";
import { ChartData, ChartGroupDataValues } from "../interfaces/chart-data";
import { Selection, BaseType } from "d3-selection";
import { scaleBand, scaleLinear, ScaleBand, ScaleLinear } from "d3-scale";
import { stackOffsetDiverging } from "d3-shape";
import { stack, min, max, sum } from "d3";
import { debounce } from "../../../utils/debounce";
import { transform } from "../../../utils/transform";
import { ChartState } from "../interfaces/chart-state";
import {
    ChartDataGroupConfig,
    BarChartDataGroupConfigItem,
} from "../interfaces/chart-data-group-config";
import { getUniqueId } from "../../../utils/unique-id";
import { ChartMargin } from "../interfaces/chart-margin";
import { BarChartSvg } from "./bar-chart-svg";
import { getCssClassForKey, getLabelForKey } from "../utils/group-config-utils";

export type BarDataItem = number[] & {
    data: ChartGroupDataValues;
    dataGroupKey: string;
};
export type BarChartGroupItem = BarDataItem[] & { index: number; key: string };
export type BarChartTooltipFromatterInputData = {
    key: string;
    value: number;
    values: { [groupKey: string]: number };
    hoveredGroupKey: string;
};
export type TargetValues = {
    [dataGroupKey: string]: number;
};

@Component({
    tag: "uic-bar-chart",
    styleUrl: "bar-chart.scss",
})
export class BarChart {
    /** The data prop for binding. Dictionary where each key is one bar and each key in it's value is one subgroup/stack group */
    @Prop() data: ChartData;

    /** The groupConfig property for binding the chart, it contains a CSS class and label field.
     * The CSS class is used for coloring the chart.
     */
    @Prop() groupConfig: ChartDataGroupConfig<
        ChartGroupDataValues,
        BarChartDataGroupConfigItem
    >;

    /** Target values for each x axis entry of `data` */
    @Prop() targetValues: TargetValues = {};

    /** Number of ticks (entries) on the Y axis.
     * In some cases d3 overwrites this value and chooses automatically
     * the best, depending on the current width and height of the chart.
     */
    @Prop() yTicks: number = 6;

    /** Label for the Y axis. */
    @Prop() yAxisLabel: string;

    /** X axis formatter, set a custom format function for x axis ticks. */
    @Prop() xFormatter: (x: string) => string;

    /** Y axis formatter, set a custom format function for y axis ticks. As D3 automatically chooses human-friendly ticks it might be more useful to change the keys in your data object. */
    @Prop() yFormatter: (x: number) => string;

    /** Whether tooltip should be show on hover/touchmove */
    @Prop() disableTooltip: boolean = false;

    /** Tooltip text formatter. Gets the x axis key and values for all data points at that location as input.
     * There's a default formatter which sorts by value, uses x and y formatters and adds a color dot per group by setting the group css class
     */
    @Prop() tooltipFormatter: (
        x: BarChartTooltipFromatterInputData
    ) => string = this.defaultTooltipFormatter.bind(this);
    /** Customize the margin. You don't have to change them, labels won't be truncated regardless of these values */
    @Prop() margin: ChartMargin = { top: 20, right: 20, bottom: 20, left: 20 };

    /** Can be used to define a custom bar chart root class for styling purposes.*/
    @Prop() barChartRootClass: string = "";

    /** State in which the bar chart is currently in. */
    @Prop() state: ChartState = ChartState.SUCCESS;

    /** Text for displaying when no data is available. */
    @Prop() noDataText: string = "No data available";

    /** Set the transition duration. As long as the transitions do not cause any issues, keep the default */
    @Prop() transitionDurationMs: number = 500;

    /** id attribute for this chart. A unique id will be generated if this is empty */
    @Prop({ mutable: true }) chartId?: string = "";

    @Element() el: HTMLUicBarChartElement;

    private readonly debouncedUpdateSvg = debounce(() => this.updateSvg(), 100);

    @Listen("resize", { target: "window" })
    onResize() {
        this.debouncedUpdateSvg();
    }

    private svg: BarChartSvg;
    private yAxisLabelOffset: number = 30;
    private xAxisLabelRotationDegrees = 65;
    private tooltip: HTMLUicTooltipElement;

    constructor() {}

    componentDidLoad() {
        this.initSvg();
    }

    @Watch("chartId")
    componentWillLoad() {
        const { chartId } = this;
        this.chartId = chartId || getUniqueId();
    }

    componentDidRender() {
        this.updateSvg();
    }

    private initSvg() {
        const {
            width: parentWidth,
            height: parentHeight,
        } = this.el.parentElement.getBoundingClientRect();

        this.svg = new BarChartSvg(this.chartId, parentWidth, parentHeight);

        // define static svg filter for in bar label background
        this.svg.appendInBarLabelBackgroundFilter();

        const chartArea = this.svg.appendChartArea(this.margin);
        chartArea.appendYAxis();
        chartArea.appendXAxis(parentHeight, this.margin);
        chartArea.appendDataGroupArea();

        this.svg.appendYAxisLabelArea();

        this.updateSvg();
    }

    private updateSvg() {
        if (this.svg === undefined) return;

        if (
            this.state !== ChartState.NODATA &&
            this.data !== undefined &&
            this.data !== null &&
            Object.keys(this.data).length > 0
        ) {
            this.svg.show();
            this.updateChart();
        } else {
            this.svg.hide();
        }
    }

    private updateChart() {
        const {
            width: parentWidth,
            height: parentHeight,
        } = this.el.parentElement.getBoundingClientRect();
        const yAxisLabelOffset = BarChart.hasYAxisLabel(this.yAxisLabel)
            ? this.yAxisLabelOffset
            : 0;
        const yAxisLabelData = BarChart.hasYAxisLabel(this.yAxisLabel)
            ? [this.yAxisLabel]
            : [];

        const valuesByStack = BarChart.dataValuesByXAxisStack(this.data);
        const minYValue = BarChart.minYValue(valuesByStack);
        const maxYValue = BarChart.maxYValue(valuesByStack);

        const maxYTickLabelWidth = BarChart.maxYTickLabelWidth(
            this.svg.chartArea.getYAxisArea(),
            maxYValue,
            this.yFormatter
        );

        const xAxisWidth = BarChart.xScaleWidth(
            parentWidth,
            this.margin,
            maxYTickLabelWidth,
            yAxisLabelOffset
        );
        const xScale = BarChart.xScale(this.data, xAxisWidth);
        const maxXTickLabelWidth = BarChart.maxXTickLabelWidth(
            this.svg.chartArea.getXAxisArea(),
            this.data,
            this.xFormatter
        );
        const additionalXTickLabelSpace =
            maxXTickLabelWidth > xScale.bandwidth() * 1.1
                ? maxXTickLabelWidth *
                  Math.sin(this.xAxisLabelRotationDegrees * (Math.PI / 180))
                : 0;
        const yScale = BarChart.yScale(
            minYValue,
            maxYValue,
            parentHeight -
                this.margin.bottom -
                this.margin.top -
                additionalXTickLabelSpace
        );

        this.svg
            .updateDimensions(
                parentWidth,
                parentHeight,
                this.transitionDurationMs
            )
            .updateYAxisLabel(
                yAxisLabelData,
                this.margin,
                parentHeight,
                this.transitionDurationMs
            )
            .chartArea.updateChartArea(
                this.margin,
                yAxisLabelOffset,
                maxYTickLabelWidth
            )
            .updateYAxis(
                yScale,
                this.yTicks,
                this.yFormatter,
                xAxisWidth,
                this.transitionDurationMs
            )
            .updateXAxis(
                xScale,
                parentHeight,
                maxXTickLabelWidth,
                this.xAxisLabelRotationDegrees,
                additionalXTickLabelSpace,
                this.margin,
                this.xFormatter,
                this.transitionDurationMs
            )
            .updateTargetValues(
                this.data,
                this.targetValues,
                xScale,
                yScale,
                this.transitionDurationMs
            )
            .updateDataGroups(BarChart.stackData(this.data), this.groupConfig)
            .updateBars(xScale, yScale, this.transitionDurationMs)
            .addTooltipHandler(
                this.tooltip,
                xScale,
                yScale,
                this.margin.left +
                    yAxisLabelOffset +
                    maxYTickLabelWidth +
                    xScale.bandwidth() / 2,
                this.margin.top,
                this.tooltipFormatter
            )
            .updateInBarLabels(
                xScale,
                yScale,
                this.yFormatter,
                this.transitionDurationMs
            );
    }

    /** Default tooltip formatter, sorts by value, uses x and y formatters and adds a color dot per group by setting the group css class */
    private defaultTooltipFormatter(d: BarChartTooltipFromatterInputData) {
        const formattedXKey = transform(this.xFormatter, d.key);
        const dataGroupsAndValues = Object.entries(d.values)
            .reverse()
            .reduce((s, [itemGroupKey, value]) => {
                const cssClass = getCssClassForKey(
                    this.groupConfig,
                    itemGroupKey
                );
                const dataGroup = getLabelForKey(
                    this.groupConfig,
                    itemGroupKey
                );
                const formattedYValue = transform(this.yFormatter, value);
                return (
                    s +
                    `<div style="white-space: nowrap;"><span class="${cssClass} uic-bar-chart__tooltip-color-indicator"></span><span ${
                        itemGroupKey === d.hoveredGroupKey
                            ? 'style="font-weight: bold"'
                            : ""
                    }>${dataGroup}: ${formattedYValue}</span></div>`
                );
            }, "");

        return `<div><b>${formattedXKey}</b></div><div>${dataGroupsAndValues}</div>`;
    }

    private static hasYAxisLabel(yAxisLabel: string) {
        return yAxisLabel && yAxisLabel.trim().length > 0 ? true : false;
    }

    private static xScale(data: ChartData, width: number): ScaleBand<string> {
        return scaleBand()
            .domain(BarChart.xAxisEntries(data))
            .range([0, width])
            .padding(0.2);
    }

    private static maxXTickLabelWidth(
        xAxisArea: Selection<SVGElement, unknown, BaseType, unknown>,
        data: ChartData,
        xFormatter: (x: string) => string
    ) {
        const longestKey = BarChart.xAxisEntries(data)
            .map((k) => transform(xFormatter, k))
            .reduce(
                (max, k) =>
                    k.toString().length > max.length ? k.toString() : max,
                ""
            );
        // account for font size specific margin (use of em to place rotatetd labels, see above)
        const stringToMeasure = `00${longestKey}00`;

        const dummyLabel = xAxisArea.append("text").text(stringToMeasure);
        const textLength = dummyLabel.node().getComputedTextLength();
        dummyLabel.remove();

        return textLength;
    }

    private static yScale(
        minYValue: number,
        maxYValue: number,
        height: number
    ): ScaleLinear<number, number> {
        return scaleLinear().domain([minYValue, maxYValue]).range([height, 0]);
    }

    private static maxYTickLabelWidth(
        yAxisArea: Selection<SVGElement, unknown, BaseType, unknown>,
        maxYValue: number,
        yFormatter: (x: number) => string
    ) {
        const dummyLabel = yAxisArea
            .append("text")
            .text(transform(yFormatter, maxYValue));
        const textLength = dummyLabel.node().getComputedTextLength();
        dummyLabel.remove();

        return textLength;
    }

    private static xAxisEntries(data: ChartData) {
        return Object.keys(data);
    }

    private static maxYValue(valuesByStack: number[][]) {
        const totalOfEachPositiveBarStack = valuesByStack.map((k) =>
            sum([0, ...k].filter((k) => k >= 0))
        );
        return +max(totalOfEachPositiveBarStack);
    }

    private static minYValue(valuesByStack: number[][]) {
        const totalOfEachNegativeBarStack = valuesByStack.map((k) =>
            sum([0, ...k].filter((k) => k <= 0))
        );
        return +min(totalOfEachNegativeBarStack);
    }

    private static dataValuesByXAxisStack(data: ChartData): number[][] {
        return BarChart.xAxisEntries(data).map((key) =>
            Object.values(data[key])
        );
    }

    /* Stacked data used for d3 arrangement of the bar charts */
    private static stackData(data: ChartData): BarChartGroupItem[] {
        const dataGroups = Object.keys(
            Object.values(data).reduce(
                (groups, current) => Object.assign(groups, current),
                {}
            )
        );

        return stack().offset(stackOffsetDiverging).keys(dataGroups)(
            Object.entries(data).map(([key, value]) =>
                Object.assign(
                    {
                        groupKey: key,
                    },
                    value
                )
            )
        );
    }

    private static xScaleWidth(
        parentWidth: number,
        margin: ChartMargin,
        yAxisTickWidth: number,
        yAxisLabelOffset: number
    ): number {
        return (
            parentWidth -
            (margin.left + margin.right + yAxisTickWidth + yAxisLabelOffset)
        );
    }

    render() {
        return (
            <Host>
                <div
                    id={this.chartId}
                    class={`uic-bar-chart  ${this.barChartRootClass}`}
                >
                    {/* Add the tooltip element outside of the svg as it's an HTML element */}
                    {!this.disableTooltip && (
                        <uic-tooltip
                            ref={(el) => (this.tooltip = el)}
                            class="uic-bar-chart__tooltip"
                            placement="top-end"
                        >
                            <div></div>
                            <div
                                slot="tip"
                                class="uic-bar-chart__tooltip-content"
                            ></div>
                        </uic-tooltip>
                    )}

                    {this.state == ChartState.LOADING && (
                        <div class="uic-bar-chart__loading_indicator">
                            <uic-loader></uic-loader>
                        </div>
                    )}
                    {(this.state === ChartState.NODATA ||
                        (this.state !== ChartState.LOADING &&
                            (this.data == undefined ||
                                this.data == null ||
                                Object.keys(this.data).length === 0))) && (
                        <div class="uic-bar-chart__no-data-indicator">
                            <div>{this.noDataText}</div>
                        </div>
                    )}
                </div>
            </Host>
        );
    }
}
